import { useState, useEffect } from "react";
import {
  Flex, Box, VStack, Text, Slider,
  SliderTrack, SliderFilledTrack, SliderThumb,
  NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper,
  Checkbox, Tooltip, IconButton, Textarea
} from "@chakra-ui/react";
import { FiRepeat } from "react-icons/fi";
import PageHeading from "../../components/PageHeading";
import CopyButton from "../../components/Button/Copy";
import ExportButton from "../../components/Button/Export";

const charset = {
  uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  lowercase: "abcdefghijklmnopqrstuvwxyz",
  numbers: "0123456789",
  symbols: "!@#$%^&*()_+~`|}{[]:;?><,./-=",
};

const PasswordGenerator = () => {
  const [length, setLength] = useState(12);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [passwordCount, setPasswordCount] = useState(1);
  const [generatedPasswords, setGeneratedPasswords] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    generatePasswords();
    // eslint-disable-next-line
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols, passwordCount]);

  const generatePasswords = () => {
    const selectedCharsets = [
      includeUppercase && charset.uppercase,
      includeLowercase && charset.lowercase,
      includeNumbers && charset.numbers,
      includeSymbols && charset.symbols,
    ].filter(Boolean);
    
    const availableChars = selectedCharsets.join("");

    if (!availableChars) {
      setGeneratedPasswords([""]);
      return;
    }

    if (passwordCount > 5000) {
      setIsGenerating(true);
      const worker = new Worker(new URL("./webworkers.js", import.meta.url));

      worker.postMessage({ length, passwordCount, availableChars });

      worker.onmessage = (e) => {
        setGeneratedPasswords(e.data);
        setIsGenerating(false);
        worker.terminate();
      };

      return;
    }

    const newPasswords = Array.from({ length: passwordCount }, () =>
      Array.from({ length }, () => availableChars[Math.floor(Math.random() * availableChars.length)]).join("")
    );

    setGeneratedPasswords(newPasswords);
  };

  return (
    <VStack spacing={6} align="stretch">
      <PageHeading
        mainText="Password Generator"
        subText="A free online tool to bulk create thousands of secure passwords and export them as an Excel or CSV file."
      />
      <Flex direction={{ base: "column", md: "row" }} gap={8} justifyContent={"center"} mt={"12px"}>
        <VStack align="start" flex={1} maxWidth="500px">
          <Flex gap={1} justifyContent={"flex-end"} alignItems={"center"} width={"100%"}>
            <Tooltip shouldWrapChildren hasArrow placement='top' label="Regenerate passwords">
              <IconButton
                aria-label="Regenerate passwords"
                variant={"ghost"}
                rounded="full"
                onClick={generatePasswords}
                isLoading={isGenerating}
              >
                <FiRepeat />
              </IconButton>
            </Tooltip>
            <CopyButton text={generatedPasswords.join("\n")} />
            <ExportButton
              data={generatedPasswords}
              formatCsv={(data) => data.map(row => `"${row}"`).join("\n")}
              formatExcel={(data) => data.map(row => [row])}
              filename="passwords"
            />
          </Flex>
          <Textarea
            value={generatedPasswords.join("\n")}
            height="100%" resize="vertical" maxHeight={"80vh"}
            onChange={(e) => setGeneratedPasswords(e.target.value.split("\n"))}
          />

        </VStack>

        <VStack align="start" maxWidth="300px">
          <Box mb={5}>
            <Text mb={2}>Number of Passwords: </Text>
            <NumberInput min={1} max={500000} value={passwordCount} onChange={(_, value) => setPasswordCount(value || 0)}>
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </Box>

          <Box width="100%">
            <Text mb={2}>Password Length:</Text>
            <Flex direction={{ base: "column", md: "row" }} gap={4} justifyContent={"space-between"}>
              <Slider min={1} max={100} value={length} onChange={(value) => setLength(value)} mb={2}>
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
              <NumberInput min={4} max={50} value={length} onChange={(_, value) => setLength(value)} maxWidth={"80px"}>
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </Flex>
          </Box>

          <VStack align="start">
            <Checkbox isChecked={includeUppercase} onChange={(e) => setIncludeUppercase(e.target.checked)}>
              Include Uppercase
            </Checkbox>
            <Checkbox isChecked={includeLowercase} onChange={(e) => setIncludeLowercase(e.target.checked)}>
              Include Lowercase
            </Checkbox>
            <Checkbox isChecked={includeNumbers} onChange={(e) => setIncludeNumbers(e.target.checked)}>
              Include Numbers
            </Checkbox>
            <Checkbox isChecked={includeSymbols} onChange={(e) => setIncludeSymbols(e.target.checked)}>
              Include Symbols
            </Checkbox>
          </VStack>

        </VStack>
      </Flex>
    </VStack>
  );
};

export default PasswordGenerator;
